import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import {
  Container,
  Box,
  Typography,
  TextField,
  Grid,
  Autocomplete,
  Collapse,
  IconButton,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FolderIcon from '@mui/icons-material/Folder';
import BreadcrumbNav from '../components/BreadcrumbNav';
import CancelButton from '../components/CancelButton';
import SubmitButton from '../components/SubmitButton';
import FileAutocomplete from '../components/FileAutocomplete';
import { textFieldStyles } from '../styles/textFieldStyles';
import { fetchFiles } from '../redux/actions/fileActions';
import { getMyFolders } from '../redux/actions/folderActions';
import { useWorkflowPrompt } from '../contexts/WorkflowPromptContext';

const UploadDocumentFromTemplate = () => {
  const { t } = useTranslation(['pages', 'forms', 'buttons', 'messages', 'navigation', 'common']);
  const { addPendingPrompt } = useWorkflowPrompt();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userLoaded);
  const { files: caseFiles } = useSelector((state) => state.fileList);
  const { folders: userFolders } = useSelector((state) => state.folderByOwner);

  // State management
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCaseFile, setSelectedCaseFile] = useState(null);
  const [activeFolder, setActiveFolder] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [moreOptionsExpanded, setMoreOptionsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    folder: '',
    owner: user?.relationship || '',
  });

  // Breadcrumbs state
  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('navigation:home'), href: '/' },
    { label: t('common:documents'), href: '/documents' },
  ]);

  // Filter files based on user role
  const filteredFiles = (caseFiles || []).filter((file) => {
    if (user?.role === 'Manager') {
      return true;
    } else if (user?.role === 'Responsible Lawyer') {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user?.relationship &&
          member.role === 'Responsible Lawyer'
      );
    } else if (user?.role === 'Assistant Lawyer') {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user?.relationship &&
          member.role === 'Treating Lawyer'
      );
    }
    return false;
  });

  // Fetch templates
  useEffect(() => {
    fetchTemplates();
    dispatch(fetchFiles());
    if (user) {
      dispatch(getMyFolders(user.relationship));
    }
  }, [dispatch, user]);

  // Initialize case file from location state
  useEffect(() => {
    if (location.state?.caseId) {
      const initialCaseFile = filteredFiles.find(
        (file) => file._id === location.state.caseId
      );
      if (initialCaseFile) {
        setSelectedCaseFile(initialCaseFile);
      }
    }
  }, [location.state, filteredFiles]);

  // Filter templates based on folder and search
  useEffect(() => {
    let filtered = templates;

    // Filter by folder
    if (activeFolder !== 'all') {
      filtered = filtered.filter((template) => template.folder === activeFolder);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          template.name.toLowerCase().includes(searchLower) ||
          (template.description &&
            template.description.toLowerCase().includes(searchLower))
      );
    }

    setFilteredTemplates(filtered);
  }, [templates, activeFolder, searchTerm]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/document-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setSnackbarMessage(t('messages:fetching_templates'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCaseFileChange = (_, value) => {
    setSelectedCaseFile(value);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData((prev) => ({
      ...prev,
      name: template.name,
      folder: template.folder || '',
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateDocument = async () => {
    // Validation
    if (!selectedCaseFile) {
      setSnackbarMessage(t('messages:please_select_case_file'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!selectedTemplate) {
      setSnackbarMessage(t('messages:please_select_template'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!formData.name.trim()) {
      setSnackbarMessage(t('messages:error.template_name_required'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setGenerating(true);
      const response = await axios.post(
        `/api/document-templates/${selectedTemplate._id}/generate`,
        {
          caseFile: selectedCaseFile._id,
          name: formData.name,
          description: formData.description,
          folder: formData.folder,
          owner: formData.owner,
        }
      );

      // Check for pending workflows and add to prompt queue
      if (response.data?.pendingWorkflows && Array.isArray(response.data.pendingWorkflows)) {
        response.data.pendingWorkflows.forEach(pending => {
          addPendingPrompt(pending);
        });
      }

      setSnackbarMessage(t('messages:document_created_successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Navigate to documents list or case documents
      setTimeout(() => {
        navigate(`/files/${selectedCaseFile._id}/documents`);
      }, 1500);
    } catch (error) {
      console.error('Error creating document:', error);
      setSnackbarMessage(
        error.response?.data?.message ||
          t('messages:error.creating_document')
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setGenerating(false);
    }
  };

  const folderCategories = [
    { key: 'all', label: t('pages:upload_document_from_template.recent_templates') },
    { key: 'Correspondence', label: t('forms:folders.correspondence') },
    { key: 'Financial', label: t('forms:folders.financial') },
    { key: 'Medical', label: t('forms:folders.medical') },
    { key: 'Other', label: t('forms:folders.other') },
  ];

  return (
    <Container
      maxWidth="lg"
      style={{ position: 'relative', paddingBottom: '80px' }}
    >
      <BreadcrumbNav
        title={t('pages:upload_document_from_template.title')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: 'white',
          padding: '30px 24px',
          marginTop: '24px',
        }}
      >
        {/* Main Content Area - Two Column Layout */}
        <Box sx={{ display: 'flex', gap: 0, height: '350px', border: '1px solid #d1d1d1', padding: 0 }}>
          {/* Left Sidebar - Template Folder Categories */}
          <Box
            sx={{
              width: '240px',
              borderRight: '1px solid #d1d1d1',
              pr: 2,
              pl: 2,
              pt: 1,
              backgroundColor: '#F5F5F5',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 1,
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {t('pages:upload_document_from_template.template_folder')}
            </Typography>
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {folderCategories.map((category) => (
                <Box
                  key={category.key}
                  sx={{
                    mb: 1,
                    border: '1px solid #d1d1d1',
                    borderRadius: '0px',
                    backgroundColor: 'white',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    onClick={() => setActiveFolder(category.key)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 0.5,
                      cursor: 'pointer',
                      backgroundColor: activeFolder === category.key ? '#e3f2fd' : 'white',
                      '&:hover': {
                        backgroundColor: activeFolder === category.key ? '#e3f2fd' : '#f5f5f5',
                      },
                      borderBottom: activeFolder === category.key ? '1px solid #d1d1d1' : 'none',
                    }}
                  >
                    <IconButton size="small" sx={{ mr: 1, p: 0.5, pointerEvents: 'none' }}>
                      {activeFolder === category.key ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </IconButton>
                    <FolderIcon sx={{ mr: 1, color: '#2B80B6', fontSize: '18px' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '12px',
                        flex: 1,
                        fontWeight: activeFolder === category.key ? 500 : 400,
                      }}
                    >
                      {category.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Section - Template List */}
          <Box sx={{ flex: 1, backgroundColor: 'white', p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 2 }}>
            {/* Search Bar */}
            <TextField
              fullWidth
              size="small"
              placeholder={t('pages:upload_document_from_template.search_templates')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#999' }} />,
              }}
              sx={{
                ...textFieldStyles(),
              }}
            />

            {/* Template Table */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ flex: 1, overflowY: 'auto', minHeight: 0, border: '1px solid #e0e0e0' }}>
                <Table size="small">
                  <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          padding: '4px 16px',
                          fontSize: '12px',
                          width: '35%',
                          '&:hover': {
                            backgroundColor: '#e3f2fd',
                          },
                        }}
                      >
                        {t('forms:template.name')}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600,
                          borderBottom: '1px solid #e0e0e0',
                          padding: '4px 16px',
                          fontSize: '12px',
                          width: '65%',
                          '&:hover': {
                            backgroundColor: '#e3f2fd',
                          },
                        }}
                      >
                        {t('forms:upload_document.description')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTemplates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ borderBottom: '1px solid #e0e0e0', padding: '4px 16px' }}>
                          {t('messages:no_templates_found')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTemplates.map((template) => (
                        <TableRow
                          key={template._id}
                          onClick={() => handleTemplateSelect(template)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor:
                              selectedTemplate?._id === template._id
                                ? '#e3f2fd'
                                : 'transparent',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                          }}
                        >
                          <TableCell 
                            sx={{ 
                              borderBottom: '1px solid #e0e0e0',
                              borderRight: '1px solid #e0e0e0',
                              padding: '4px 16px',
                              fontSize: '12px',
                            }}
                          >
                            {template.name}
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              borderBottom: '1px solid #e0e0e0',
                              padding: '4px 16px',
                              fontSize: '12px',
                            }}
                          >
                            {template.description || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>

        {/* Document Details Section */}
        <Box sx={{ mt: 5 }}>
        <Grid container spacing={2}>
          {/* Case File Selection */}
          <Grid item xs={12}>
            <FileAutocomplete
              options={filteredFiles}
              value={selectedCaseFile}
              onChange={handleCaseFileChange}
              label={t('pages:upload_document_from_template.case_file')}
              required
            />
          </Grid>

          {/* Template Selection and Name in same row */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={templates}
              value={selectedTemplate}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleTemplateSelect(newValue);
                } else {
                  setSelectedTemplate(null);
                  setFormData((prev) => ({ ...prev, name: '' }));
                }
              }}
              getOptionLabel={(option) => option.name || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('forms:labels.template')}
                  size="small"
                  sx={textFieldStyles()}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('forms:labels.document_name')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              size="small"
              sx={textFieldStyles()}
              required
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('forms:upload_document.description')}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              size="small"
              multiline
              rows={3}
              sx={textFieldStyles()}
            />
          </Grid>

          {/* More Options Section */}
          <Grid item xs={12}>
            <Box
              onClick={() => setMoreOptionsExpanded(!moreOptionsExpanded)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mb: 1,
              }}
            >
              <IconButton size="small">
                {moreOptionsExpanded ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('pages:upload_document_from_template.more_options')}
              </Typography>
            </Box>
            <Collapse in={moreOptionsExpanded}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" sx={textFieldStyles()}>
                    <InputLabel>
                      {t('forms:upload_document.folder')}
                    </InputLabel>
                    <Select
                      value={formData.folder}
                      onChange={(e) =>
                        handleInputChange('folder', e.target.value)
                      }
                      label={t('forms:upload_document.folder')}
                    >
                      <MenuItem value="Correspondence">
                        {t('forms:folders.correspondence')}
                      </MenuItem>
                      <MenuItem value="Financial">
                        {t('forms:folders.financial')}
                      </MenuItem>
                      <MenuItem value="Medical">
                        {t('forms:folders.medical')}
                      </MenuItem>
                      <MenuItem value="Other">
                        {t('forms:folders.other')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('forms:upload_document.owner')}
                    value={formData.owner}
                    disabled
                    size="small"
                    sx={textFieldStyles()}
                    helperText={t('forms:upload_document.owner_helper')}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>

        </Grid>
        </Box>
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          zIndex: 10,
          backgroundColor: 'white',
          padding: '13px 80px',
        }}
      >
        <CancelButton variant="outlined" onClick={() => navigate(-1)} size="small" disabled={generating}>
          {t('buttons:secondary.cancel')}
        </CancelButton>
        <SubmitButton onClick={handleCreateDocument} size="small" disabled={generating || !selectedCaseFile || !selectedTemplate}>
          {generating ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            t('pages:upload_document_from_template.create_document')
          )}
        </SubmitButton>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UploadDocumentFromTemplate;
